
import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  ArrowUpRight,
  Building2,
  ChevronDown,
  HardHat,
  House,
  Layers3,
  Menu,
  MessageCircle,
  Paintbrush,
  Phone,
  X,
} from "lucide-react";
import logo from "../../assests/cubelogo.webp";

export default function Navigation() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 15);

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";

    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsOpen(false);
        setServicesOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsOpen(false);
        setServicesOpen(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const navLinks = [
    { name: "Home", path: "/" },
    { name: "About Us", path: "/about" },
    { name: "Portfolio", path: "/#portfolio" },
    { name: "Contact", path: "/contact" },
  ];

  const serviceItems = [
    {
      number: "01",
      name: "Interior Design",
      description: "Beautifully considered living spaces.",
      icon: House,
      path: "/",
    },
    {
      number: "02",
      name: "Construction",
      description: "Quality construction, built to last.",
      icon: HardHat,
      path: "/",
    },
    {
      number: "03",
      name: "Painting & Finishing",
      description: "The finishing touch to your space.",
      icon: Paintbrush,
      path: "/",
    },
    {
      number: "04",
      name: "Turnkey Solutions",
      description: "One team from concept to completion.",
      icon: Layers3,
      path: "/",
    },
  ];

  const closeMenus = () => {
    setIsOpen(false);
    setServicesOpen(false);
  };

  const handleQuote = () => {
    closeMenus();

    const quoteSection = document.getElementById("quote");

    if (quoteSection) {
      quoteSection.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    } else {
      navigate("/contact");
    }
  };

  const handlePortfolio = (event) => {
    event.preventDefault();
    closeMenus();

    if (window.location.pathname !== "/") {
      navigate("/");
      window.setTimeout(() => {
        document.getElementById("portfolio")?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 200);
      return;
    }

    document.getElementById("portfolio")?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <header className="fixed inset-x-0 top-0 z-50 w-full">
      {/* Top information strip */}
      <div
        className={`hidden overflow-hidden bg-[#171a20] text-white transition-all duration-300 lg:block ${
          scrolled ? "h-0 opacity-0" : "h-[36px] opacity-100"
        }`}
      >
        <div className="mx-auto flex h-full max-w-[1600px] items-center justify-between px-6 xl:px-12 2xl:px-16">
          <p className="font-['DM_Sans',sans-serif] text-[10px] font-medium tracking-[1.2px] text-stone-300 xl:text-[11px]">
            DESIGNING SPACES. BUILDING POSSIBILITIES.
          </p>

          <div className="flex items-center gap-5 xl:gap-7">
            <span className="inline-flex items-center gap-2 font-['DM_Sans',sans-serif] text-[11px] text-stone-300">
              <span className="h-1.5 w-1.5 rounded-full bg-blue-400" />
              Interior Design & Construction
            </span>

            <a
              href="https://wa.me/919876543210"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 font-['DM_Sans',sans-serif] text-[11px] font-semibold text-white transition-colors hover:text-blue-300"
            >
              <MessageCircle size={13} />
              Connect with us
              <ArrowUpRight size={12} />
            </a>
          </div>
        </div>
      </div>

      {/* Main navigation */}
      <div
        className={`relative border-b transition-all duration-300 ${
          scrolled
            ? "border-stone-200 bg-white/95 shadow-[0_8px_30px_rgba(20,25,35,0.07)] backdrop-blur-xl"
            : "border-stone-200/70 bg-white"
        }`}
      >
        <nav
          aria-label="Main navigation"
          className="mx-auto flex min-h-[76px] w-full max-w-[1600px] items-center justify-between gap-3 px-4 sm:px-6 lg:min-h-[84px] lg:px-8 xl:px-12 2xl:px-16"
        >
          {/* Logo and brand */}
          <Link
            to="/"
            onClick={closeMenus}
            aria-label="Cube4Spaces home"
            className="group flex min-w-0 shrink-0 items-center gap-2.5 sm:gap-3.5"
          >
            <span className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-stone-200 bg-white p-1.5 transition-all duration-300 group-hover:border-blue-300 sm:h-12 sm:w-12">
              <img
                src={logo}
                alt="Cube4Spaces logo"
                className="h-full w-full object-contain"
              />
            </span>

            <span className="flex min-w-0 flex-col">
              <span className="whitespace-nowrap font-['Poppins',sans-serif] text-[16px] font-bold leading-tight tracking-[-0.6px] text-[#1b1d22] sm:text-xl xl:text-[22px]">
                CUBE4SPACES<span className="text-blue-600">.</span>
              </span>

              <span className="mt-1 whitespace-nowrap font-['DM_Sans',sans-serif] text-[8px] font-bold uppercase tracking-[1.4px] text-stone-500 sm:text-[9px] sm:tracking-[2px]">
                Design · Build · Handover
              </span>
            </span>
          </Link>

          {/* Desktop navigation */}
          <div className="hidden items-center lg:flex">
            {navLinks.map((link) => {
              if (link.name === "Portfolio") {
                return (
                  <a
                    key={link.name}
                    href="/#portfolio"
                    onClick={handlePortfolio}
                    className="group relative whitespace-nowrap px-3 py-3 font-['DM_Sans',sans-serif] text-[13px] font-semibold text-stone-600 transition-colors hover:text-blue-700 xl:px-4"
                  >
                    {link.name}
                    <span className="absolute bottom-1 left-3 right-3 h-[2px] origin-left scale-x-0 bg-blue-600 transition-transform duration-300 group-hover:scale-x-100 xl:left-4 xl:right-4" />
                  </a>
                );
              }

              return (
                <NavLink
                  key={link.name}
                  to={link.path}
                  end={link.path === "/"}
                  onClick={closeMenus}
                  className={({ isActive }) =>
                    `group relative whitespace-nowrap px-3 py-3 font-['DM_Sans',sans-serif] text-[13px] font-semibold transition-colors xl:px-4 ${
                      isActive
                        ? "text-blue-700"
                        : "text-stone-600 hover:text-blue-700"
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      {link.name}
                      <span
                        className={`absolute bottom-1 left-3 right-3 h-[2px] origin-left bg-blue-600 transition-transform duration-300 xl:left-4 xl:right-4 ${
                          isActive
                            ? "scale-x-100"
                            : "scale-x-0 group-hover:scale-x-100"
                        }`}
                      />
                    </>
                  )}
                </NavLink>
              );
            })}

            {/* Services dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setServicesOpen(true)}
              onMouseLeave={() => setServicesOpen(false)}
            >
              <button
                type="button"
                aria-haspopup="true"
                aria-expanded={servicesOpen}
                onClick={() => setServicesOpen((previous) => !previous)}
                className={`inline-flex items-center gap-2 whitespace-nowrap px-3 py-3 font-['DM_Sans',sans-serif] text-[13px] font-semibold transition-colors xl:px-4 ${
                  servicesOpen
                    ? "text-blue-700"
                    : "text-stone-600 hover:text-blue-700"
                }`}
              >
                Services
                <ChevronDown
                  size={14}
                  strokeWidth={1.8}
                  className={`transition-transform duration-300 ${
                    servicesOpen ? "rotate-180" : ""
                  }`}
                />
              </button>

              {/* Desktop dropdown */}
              <div
                className={`absolute right-0 top-full z-50 w-[370px] pt-4 transition-all duration-200 ${
                  servicesOpen
                    ? "visible translate-y-0 opacity-100"
                    : "invisible -translate-y-2 opacity-0"
                }`}
              >
                <div className="overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-[0_22px_65px_rgba(20,25,35,0.14)]">
                  <div className="bg-[#171a20] px-6 py-5 text-white">
                    <p className="font-['DM_Sans',sans-serif] text-[10px] font-bold uppercase tracking-[2.5px] text-blue-300">
                      What we do
                    </p>
                    <h3 className="mt-2 font-['Poppins',sans-serif] text-xl font-semibold">
                      Spaces, thoughtfully made.
                    </h3>
                    <p className="mt-1.5 font-['DM_Sans',sans-serif] text-sm leading-6 text-stone-300">
                      From the first idea to the final handover.
                    </p>
                  </div>

                  <div className="p-2.5">
                    {serviceItems.map((service) => {
                      const Icon = service.icon;

                      return (
                        <NavLink
                          key={service.name}
                          to={service.path}
                          onClick={closeMenus}
                          className="group flex items-center gap-3 rounded-xl px-3 py-3 transition-colors duration-200 hover:bg-blue-50"
                        >
                          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-stone-200 bg-white text-stone-700 transition-all group-hover:border-blue-200 group-hover:bg-blue-100 group-hover:text-blue-700">
                            <Icon size={20} strokeWidth={1.7} />
                          </span>

                          <span className="min-w-0 flex-1">
                            <span className="block font-['DM_Sans',sans-serif] text-sm font-bold text-stone-800 group-hover:text-blue-700">
                              {service.name}
                            </span>
                            <span className="mt-1 block font-['DM_Sans',sans-serif] text-xs leading-5 text-stone-500">
                              {service.description}
                            </span>
                          </span>

                          <ArrowUpRight
                            size={16}
                            className="shrink-0 text-stone-400 transition-all group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-blue-600"
                          />
                        </NavLink>
                      );
                    })}
                  </div>

                  <div className="border-t border-stone-100 px-5 py-3">
                    <button
                      type="button"
                      onClick={handleQuote}
                      className="flex w-full items-center justify-between font-['DM_Sans',sans-serif] text-xs font-bold text-stone-600 transition-colors hover:text-blue-700"
                    >
                      Discuss your project
                      <ArrowRight size={15} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Desktop CTA */}
          <div className="hidden shrink-0 lg:block">
            <button
              type="button"
              onClick={handleQuote}
              className="group inline-flex items-center gap-3 rounded-lg bg-blue-700 py-2.5 pl-5 pr-2.5 font-['DM_Sans',sans-serif] text-[13px] font-bold text-white transition-all duration-300 hover:bg-[#171a20]"
            >
              Get a Quote
              <span className="flex h-8 w-8 items-center justify-center rounded-md bg-white/15 transition-colors group-hover:bg-white/20">
                <ArrowUpRight
                  size={17}
                  className="transition-transform duration-300 group-hover:rotate-45"
                />
              </span>
            </button>
          </div>

          {/* Mobile menu button */}
          <button
            type="button"
            onClick={() => {
              setIsOpen((previous) => !previous);
              setServicesOpen(false);
            }}
            aria-label={
              isOpen ? "Close navigation menu" : "Open navigation menu"
            }
            aria-expanded={isOpen}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-stone-200 bg-white text-stone-800 transition-colors hover:border-blue-200 hover:bg-blue-50 lg:hidden"
          >
            {isOpen ? (
              <X size={22} strokeWidth={1.8} />
            ) : (
              <Menu size={22} strokeWidth={1.8} />
            )}
          </button>
        </nav>
      </div>

      {/* Mobile and tablet navigation */}
      <div
        className={`absolute inset-x-0 top-full overflow-y-auto border-b border-stone-200 bg-white shadow-[0_20px_45px_rgba(20,25,35,0.1)] transition-all duration-300 lg:hidden ${
          isOpen
            ? "visible max-h-[calc(100dvh-76px)] opacity-100"
            : "invisible max-h-0 opacity-0"
        }`}
      >
        <div className="mx-auto w-full max-w-[1600px] px-4 pb-6 pt-5 sm:px-6 md:px-8">
          {/* Mobile intro */}
          <div className="mb-5 flex items-center justify-between rounded-xl bg-[#171a20] p-4 text-white sm:p-5">
            <div>
              <p className="font-['DM_Sans',sans-serif] text-[9px] font-bold uppercase tracking-[2px] text-blue-300">
                Welcome to Cube4Spaces
              </p>
              <p className="mt-1.5 font-['Poppins',sans-serif] text-base font-semibold sm:text-lg">
                Let’s create your space.
              </p>
            </div>
            <Building2
              size={28}
              strokeWidth={1.3}
              className="shrink-0 text-blue-300"
            />
          </div>

          {/* Main links */}
          <p className="mb-2 px-3 font-['DM_Sans',sans-serif] text-[10px] font-bold uppercase tracking-[2px] text-stone-400">
            Navigation
          </p>

          <div className="space-y-1">
            {navLinks.map((link) => {
              if (link.name === "Portfolio") {
                return (
                  <a
                    key={link.name}
                    href="/#portfolio"
                    onClick={handlePortfolio}
                    className="group flex items-center justify-between rounded-xl px-3 py-3.5 font-['DM_Sans',sans-serif] text-sm font-semibold text-stone-700 transition-colors hover:bg-blue-50 hover:text-blue-700 sm:text-base"
                  >
                    {link.name}
                    <ArrowUpRight
                      size={17}
                      className="text-stone-400 transition-colors group-hover:text-blue-700"
                    />
                  </a>
                );
              }

              return (
                <NavLink
                  key={link.name}
                  to={link.path}
                  end={link.path === "/"}
                  onClick={closeMenus}
                  className={({ isActive }) =>
                    `group flex items-center justify-between rounded-xl px-3 py-3.5 font-['DM_Sans',sans-serif] text-sm font-semibold transition-colors sm:text-base ${
                      isActive
                        ? "bg-blue-50 text-blue-700"
                        : "text-stone-700 hover:bg-stone-50"
                    }`
                  }
                >
                  {link.name}
                  <ArrowUpRight
                    size={17}
                    className="text-stone-400 transition-colors group-hover:text-blue-700"
                  />
                </NavLink>
              );
            })}
          </div>

          {/* Mobile services accordion */}
          <div className="mt-5 border-t border-stone-200 pt-4">
            <button
              type="button"
              onClick={() => setServicesOpen((previous) => !previous)}
              aria-expanded={servicesOpen}
              className="flex w-full items-center justify-between px-3 py-2.5 font-['Poppins',sans-serif] text-sm font-semibold text-stone-900"
            >
              Our Services
              <ChevronDown
                size={17}
                className={`transition-transform duration-300 ${
                  servicesOpen ? "rotate-180 text-blue-700" : "text-stone-500"
                }`}
              />
            </button>

            <div
              className={`grid transition-all duration-300 ${
                servicesOpen
                  ? "mt-2 grid-rows-[1fr] opacity-100"
                  : "grid-rows-[0fr] opacity-0"
              }`}
            >
              <div className="overflow-hidden">
                <div className="space-y-1">
                  {serviceItems.map((service) => {
                    const Icon = service.icon;

                    return (
                      <NavLink
                        key={service.name}
                        to={service.path}
                        onClick={closeMenus}
                        className="group flex items-center gap-3 rounded-xl px-3 py-3 transition-colors hover:bg-blue-50"
                      >
                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-stone-100 text-stone-700 transition-colors group-hover:bg-blue-100 group-hover:text-blue-700">
                          <Icon size={18} strokeWidth={1.7} />
                        </span>

                        <span className="min-w-0 flex-1">
                          <span className="block font-['DM_Sans',sans-serif] text-sm font-semibold text-stone-800">
                            {service.name}
                          </span>
                          <span className="mt-1 block font-['DM_Sans',sans-serif] text-xs text-stone-500">
                            {service.description}
                          </span>
                        </span>

                        <ArrowUpRight
                          size={16}
                          className="text-stone-400"
                        />
                      </NavLink>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Mobile contact CTA */}
          <button
            type="button"
            onClick={handleQuote}
            className="group mt-5 flex w-full items-center justify-between rounded-xl bg-blue-700 px-4 py-4 text-left text-white transition-colors hover:bg-[#171a20] sm:px-5"
          >
            <span>
              <span className="block font-['DM_Sans',sans-serif] text-sm font-bold sm:text-base">
                Let’s talk about your project
              </span>
              <span className="mt-1 block font-['DM_Sans',sans-serif] text-xs text-blue-100">
                Get in touch with our team.
              </span>
            </span>

            <span className="ml-3 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white/15">
              <ArrowUpRight
                size={19}
                className="transition-transform group-hover:rotate-45"
              />
            </span>
          </button>

          {/* Mobile contact strip */}
          <a
            href="https://wa.me/919876543210"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-4 flex items-center justify-center gap-2 py-2 font-['DM_Sans',sans-serif] text-xs font-semibold text-stone-500 transition-colors hover:text-blue-700"
          >
            <Phone size={14} />
            Connect with us on WhatsApp
          </a>

          <p className="mt-3 text-center font-['DM_Sans',sans-serif] text-[10px] tracking-wide text-stone-400">
            DESIGN · BUILD · HANDOVER
          </p>
        </div>
      </div>
    </header>
  );
}
